import {
    Component,
    ElementRef,
    EventEmitter,
    Input,
    NgZone,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
} from '@angular/core';
import { Observable, Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';

import { AuthService } from '../../auth/auth.service';
import { TranslationKeyMap } from '../coaching-insight.i18n';

import { PersonOfInterest } from './models/coaching.global.model';
import { CoachingInsightWidgetService } from './services/coaching-insight.widget.service';
import { PersonOfInterestService } from './services/person-of-interest.service';
import { TeamOAService } from './services/team-oa.service';

@Component({
    selector: 'cmps-team-view',
    templateUrl: './team-view-selector.component.html',
})
export class TeamViewSelector implements OnInit, OnChanges, OnDestroy {
    @Input() selectedUser: PersonOfInterest;
    @Input() favourites: PersonOfInterest[];
    @Input() dropdownPosition: string;
    @Input() currentView: PersonOfInterest;
    @Output() selectedUserChange: EventEmitter<any>;
    @Output() isOpen: EventEmitter<boolean>;

    @ViewChild('arrow') arrow: ElementRef;
    @ViewChild('options') options: ElementRef;
    @ViewChild('searchBox', { static: true }) searchBox: ElementRef;

    loadingSearchResults: boolean = false;

    public name: string = 'test';
    public focused: boolean = false;
    public searchResults: PersonOfInterest[] = [];
    public searchQuery: string = null;

    public txtQueryChanged: Subject<string> = new Subject<string>();
    public searched: Subject<any> = new Subject<any>();

    private _set: boolean;

    public searchObs: Observable<PersonOfInterest[]>;
    public querySub: Subscription;
    public toggleDropdown: boolean = false;

    i18n = TranslationKeyMap;

    constructor(
        private readonly _coachInsightService: CoachingInsightWidgetService,
        private readonly _personOfInterestService: PersonOfInterestService,
        private readonly _authService: AuthService,
        private readonly _teamOAService: TeamOAService,
        private readonly _ngZone: NgZone,
        private readonly _eref: ElementRef,
    ) {
        this.dropdownPosition = 'right';
        this.selectedUserChange = new EventEmitter<any>();
        this.isOpen = new EventEmitter<boolean>();
        this._set = false;
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    get user() {
        return this._authService.user;
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    ngOnChanges(args) {
        // eslint-disable-next-line no-prototype-builtins
        if (args.hasOwnProperty('favourites')) {
            if (this._set === false) {
                const v = args.favourites.currentValue;
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                if (v && v.length) {
                    this._set = true;
                }
            }

            this.favourites.sort((a, b) => {
                return `${a.label}`.localeCompare(b.label);
            });
        }
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    ngOnInit() {
        this.searchQuery = '';

        this.querySub = this.txtQueryChanged
            .pipe(
                debounceTime(300), // wait 1 sec after the last event before emitting last event
                distinctUntilChanged(),
                switchMap((term) => {
                    this.searchQuery = term || '';

                    // Call your function which calls API or do anything you would like do after a lag of 0.3 sec
                    if (!this.searchQuery || this.searchQuery.length < 3) {
                        this.searchResults = [];
                        return [];
                    } else {
                        this.loadingSearchResults = true;
                        return this.search();
                    }
                }),
            ) // only emit if value is different from previous value
            .subscribe((data) => {
                this.loadingSearchResults = false;

                if (data) {
                    // if user already has emptied the search query and data is still arriving, don't show data
                    if (!this.searchQuery) {
                        this.searchResults = [];
                        return;
                    }
                    data = data.map((d) => {
                        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                        d.label = `${d.firstName} ${d.lastName}`;
                        d.value = d.userId.toString();
                        return d;
                    });
                    data = data.filter((f) => {
                        const c = this.favourites.find(
                            (x) => x.userId === f.userId,
                        );
                        return c == null;
                    });
                    data.sort((a, b) => {
                        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                        return `${a.label}`.localeCompare(b.label);
                    });
                    this.searchResults = data;
                }
            });

        if (this.favourites == null) {
            this.favourites = [];
        }
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    ngOnDestroy() {
        this.searchResults = [];
        this.searchObs = null;
        if (this.querySub) {
            this.querySub.unsubscribe();
        }
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    handleOpen(state: boolean) {
        if (state) {
            this.clearSearch();
            // only retrieve the logged in user's favourites once per page load
            this._personOfInterestService.retrieveFavourites();
            setTimeout(() => {
                if (this.searchBox) {
                    this.searchBox.nativeElement.focus();
                }
            }, 100);
        }
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    toggle(force: boolean) {
        this.focused = force != null ? force : !this.focused;
        if (!this.focused) {
            this.isOpen.emit(this.focused);
            this.clearSearch();
        } else {
            this.isOpen.emit(this.focused);
        }
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    selectedChange($event) {
        if (!$event) return;
        this._personOfInterestService.setFavourite($event);
        this.selectedUserChange.emit($event);
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    onQuery($event) {
        this.txtQueryChanged.next($event);
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    search() {
        // this._ngZone.runOutsideAngular(() => {

        const x = this._personOfInterestService.searchPeople(this.searchQuery);

        return x;
        // this.searchObs.subscribe(data => {
        //     // this._ngZone.run(() => {
        //         if(data) {

        //             // if user already has emptied the search query and data is still arriving, don't show data
        //             if(!this.searchQuery) {
        //                 this.searchResults = [];
        //                 return;
        //             }
        //             data.map(d => {
        //                 d.label = `${d.firstName} ${d.lastName}`;
        //                 d.value = d.userId.toString();
        //                 return d;
        //             })
        //             data = data.filter(f => {
        //                 let c = this.favourites.find(x=>x.userId == f.userId);
        //                 return c == null;
        //             })
        //             this.searchResults = data;
        //         }
        //     // });
        // });
        //   });
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    clearSearch() {
        this.onQuery(null);
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    toggleFavourites(userID) {
        this._personOfInterestService.addOrRemoveMemberToFavourites(userID);
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    setDefaultView() {
        const x = this._personOfInterestService.setDefaultFavourite();
        this.selectedUserChange.emit(x);
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    doToList(e, userID, state) {
        e.preventDefault();
        e.stopPropagation();

        const success = this._personOfInterestService.addOrRemoveMemberViewList(
            userID,
            state,
        );
        success.subscribe((result) => {
            if (result) {
                this._personOfInterestService.retrieveFavourites();
                // if the deleted favourite is the same as active, reset current view to default
                if (state === false && this.currentView.userId === userID) {
                    this.setDefaultView();
                } else if (this.searchResults.length) {
                    this.searchResults = this.searchResults.filter(
                        (f) => f.userId !== userID,
                    );
                }
            } else {
                console.error(
                    'There was an issue with this action for PersonOfInterest.',
                    result,
                );
            }
        });
    }
}
